import {
  InputRichTextQuickResponseApiType,
  QuickResponseItemT,
} from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import {
  QuerySuggestArticlesArgs,
  SuggestionsQueryVariables,
} from 'components/conversation/components/integrations/elevio/src/apis/graphQl/elevio/grappa/types/grappa.types';

import { useElevioIntegration } from '../../../../elevioIntegrationProvider';
import { useElevioQuery } from '../../../../hooks/elevio/useElevioQuery';
import { SuggestArticleQueryResult, suggestArticlesQuery } from '../api';

import { buildElevioArticleLink } from './buildElevioArticleLink';

type Params = {
  search: string;
};

type QuickResponseRenderer = (item: {
  content: string;
  name?: string;
  subject?: string;
}) => Promise<string>;

type ReturnType = {
  quickResponseApi: InputRichTextQuickResponseApiType;
  quickResponseRenderer: QuickResponseRenderer;
};

const paging: SuggestionsQueryVariables['paging'] = {
  first: 50,
  offset: 0,
};

export function useElevioArticleQuickResponse({ search }: Params): ReturnType {
  const [languageId, setLanguageId] = useState('en');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const { elevioIntegration } = useElevioIntegration();

  useDebounce(() => setDebouncedSearchTerm(search), 1000, [search]);

  const variables = useMemo(
    () => ({
      languageId: languageId,
      pageUrl: window.location.origin,
      query: { paging, text: debouncedSearchTerm },
    }),
    [debouncedSearchTerm, languageId],
  ) satisfies QuerySuggestArticlesArgs;

  const {
    data: suggestedArticlesResult,
    error,
    isLoading,
    triggerFetch,
  } = useElevioQuery<SuggestArticleQueryResult>({
    query: suggestArticlesQuery,
    variables,
  });

  const items: QuickResponseItemT[] = useMemo(
    () =>
      suggestedArticlesResult?.articleSuggestions?.results?.map?.(
        ({ title, slug, id, category }) => ({
          name: title,
          content: category?.title ?? '',
          subject:
            buildElevioArticleLink({
              id,
              slug,
              languageId,
              baseUrl: elevioIntegration?.kbLink,
            }) ?? '',
        }),
      ) ?? [],
    [suggestedArticlesResult, languageId, elevioIntegration?.kbLink],
  );

  const handleParamsChange: InputRichTextQuickResponseApiType['paramsChange'] =
    useCallback(
      async (params) => {
        if (params?.search === '') {
          setDebouncedSearchTerm(search);
        } else if (params?.search && (params.search.length ?? 0) > 0) {
          setDebouncedSearchTerm(params.search);
        }

        if (params?.locale) {
          setLanguageId(params.locale);
        }

        await triggerFetch();
        return items;
      },
      [triggerFetch, items, search],
    );

  const quickResponseRenderer: QuickResponseRenderer = useCallback(
    async ({ name = '', subject = '' }) => {
      // The quick response UI uses item.content instead of item.subject to show the item description in the list,
      // so the link is stored in item.subject to free up item.content for the description. See `items` list above.
      return `<a href="${subject}" target="_blank" rel="noopener noreferrer">${name}</a>`;
    },
    [],
  );

  const quickResponseApi = useMemo(
    () =>
      ({
        items: items,
        async getItems() {
          await triggerFetch();
          return items;
        },
        getItemsAsyncStatus: isLoading
          ? 'loading'
          : error
          ? 'failed'
          : 'succeeded',
        params: { search: debouncedSearchTerm },
        hasNextPage: false,
        paramsChange: handleParamsChange,
        nextPage: () => Promise.resolve(items),
      } satisfies InputRichTextQuickResponseApiType),
    [
      items,
      isLoading,
      error,
      debouncedSearchTerm,
      handleParamsChange,
      triggerFetch,
    ],
  );

  return useMemo(
    () => ({ quickResponseApi, quickResponseRenderer }),
    [quickResponseApi, quickResponseRenderer],
  );
}
